import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { Award, Search, Loader2, TrendingUp, BarChart3, User, Sparkles, ChevronDown } from 'lucide-react';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

const TopperformersWidget = () => {
  const [topStudents, setTopStudents] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedTest, setSelectedTest] = useState('iaTest1');
  const [studentPhotos, setStudentPhotos] = useState({});
  const [expandedSubject, setExpandedSubject] = useState(null); // Track which subject is expanded
  const MAX_HEIGHT = 'max-h-[500px]';

  // Subject code mapping
  const SUBJECT_MAP = {
    'amI': { name: 'Applied Mathematics-I', code: 'AM-I', max: 20 },
    'ap': { name: 'Applied Physics', code: 'AP', max: 15 },
    'ac': { name: 'Applied Chemistry', code: 'AC', max: 15 },
    'em': { name: 'Engineering Mechanics', code: 'EM', max: 20 },
    'bee': { name: 'Basic Electrical & Electronics', code: 'BEE', max: 20 },
    'pce': { name: 'Professional Communication & Ethics', code: 'PCE', max: 15 }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTest]);

  const fetchStudentPhoto = async (rollNumber) => {
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        return getStudentPhotoUrl(studentDoc.data()) || null;
      }
      return null;
    } catch (err) {
      console.error(`Failed to fetch photo for ${rollNumber}:`, err);
      return null;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const iaColl = collection(db, selectedTest);
      const topQ = query(iaColl, orderBy('percentage', 'desc'), limit(5));
      const topSnap = await getDocs(topQ);
      const topArr = topSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const allSnap = await getDocs(iaColl);
      const allArr = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Fetch photos for top 5 students
      const photos = {};
      for (const student of topArr) {
        if (student.rollNumber) {
          const photoUrl = await fetchStudentPhoto(student.rollNumber);
          if (photoUrl) {
            photos[student.rollNumber] = photoUrl;
          }
        }
      }

      setTopStudents(topArr);
      setAllResults(allArr);
      setStudentPhotos(photos);
    } catch (err) {
      console.error('Failed to fetch IA data:', err);
      setError('Unable to load results. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const computeSubjectStats = (resultsArray) => {
    const subjectStats = {};

    // Initialize all subjects
    Object.keys(SUBJECT_MAP).forEach(code => {
      subjectStats[code] = {
        code,
        name: SUBJECT_MAP[code].name,
        shortName: SUBJECT_MAP[code].code,
        maxMarks: SUBJECT_MAP[code].max,
        totalStudents: 0,
        totalMarks: 0,
        passed: 0,
        failed: 0,
        fullMarksCount: 0,
        fullMarksStudents: [], // Array to store students with full marks
        averageMarks: 0,
        averagePercent: 0
      };
    });

    // Process each student's subjects
    resultsArray.forEach((student) => {
      const subjects = student.subjects || {};
      
      Object.keys(subjects).forEach((subjectCode) => {
        const code = subjectCode; // No toLowerCase() - match Firebase keys exactly
        if (subjectStats[code]) {
          const marks = Number(subjects[subjectCode]) || 0;
          const maxMarks = subjectStats[code].maxMarks;
          const percent = (marks / maxMarks) * 100;
          
          subjectStats[code].totalStudents += 1;
          subjectStats[code].totalMarks += marks;
          
          // Pass threshold: 40% of max marks
          const passMarks = maxMarks * 0.4;
          if (marks >= passMarks) {
            subjectStats[code].passed += 1;
          } else {
            subjectStats[code].failed += 1;
          }

          // Count students with full marks
          if (marks === maxMarks) {
            subjectStats[code].fullMarksCount += 1;
            subjectStats[code].fullMarksStudents.push({
              studentName: student.studentName,
              rollNumber: student.rollNumber,
              division: student.division || '—'
            });
          }
        }
      });
    });

    // Calculate averages and pass percentages
    Object.keys(subjectStats).forEach(code => {
      const stat = subjectStats[code];
      if (stat.totalStudents > 0) {
        stat.averageMarks = Number((stat.totalMarks / stat.totalStudents).toFixed(2));
        stat.averagePercent = Number(((stat.averageMarks / stat.maxMarks) * 100).toFixed(2));
        stat.passPercent = Number(((stat.passed / stat.totalStudents) * 100).toFixed(1));
      }
    });

    // Convert to array and sort by pass percentage
    const statsArray = Object.values(subjectStats).filter(s => s.totalStudents > 0);
    statsArray.sort((a, b) => b.passPercent - a.passPercent);
    
    return statsArray;
  };

  const subjectStats = computeSubjectStats(allResults);

  const filteredTop = topStudents.filter((s) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      (s.studentName && s.studentName.toLowerCase().includes(q)) ||
      (s.rollNumber && String(s.rollNumber).toLowerCase().includes(q))
    );
  });

  const getGradeColor = (percent) => {
    if (percent >= 75) return 'text-emerald-600';
    if (percent >= 60) return 'text-amber-600';
    if (percent >= 40) return 'text-orange-600';
    return 'text-rose-600';
  };

  const getGradeBg = (percent) => {
    if (percent >= 75) return 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200';
    if (percent >= 60) return 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200';
    if (percent >= 40) return 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200';
    return 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200';
  };

  const getRankBadge = (idx) => {
    const badges = {
      0: 'bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-600 shadow-lg shadow-amber-500/50',
      1: 'bg-gradient-to-br from-slate-300 via-gray-400 to-slate-500 shadow-lg shadow-slate-400/50',
      2: 'bg-gradient-to-br from-orange-400 via-amber-600 to-orange-700 shadow-lg shadow-orange-500/50'
    };
    return badges[idx] || 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600';
  };

  return (
    <div
      className={`bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 rounded-2xl shadow-xl p-6 w-full ${MAX_HEIGHT} overflow-y-auto flex flex-col gap-6`}
    >
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl blur opacity-75 animate-pulse"></div>
            <div className="relative p-3 bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-600 rounded-xl shadow-lg">
              <Award className="w-7 h-7 text-white" />
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-2">
              Top Performers
              <Sparkles className="w-5 h-5 text-amber-500" />
            </h3>
            <p className="text-sm text-slate-500 font-medium">Excellence in academics</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Test Selector */}
          <select
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            className="px-4 py-2.5 rounded-xl border-2 border-indigo-100 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all"
          >
            <option value="iaTest1">IAT-1</option>
            <option value="iaTest2">IAT-2</option>
          </select>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search students..."
              className="pl-10 pr-4 py-2.5 rounded-xl border-2 border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all w-56"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="relative">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
            <div className="absolute inset-0 blur-xl bg-indigo-400 opacity-50 animate-pulse"></div>
          </div>
          <p className="text-slate-600 font-medium">Loading performance data...</p>
        </div>
      ) : error ? (
        <div className="bg-gradient-to-br from-rose-50 to-red-50 border-2 border-rose-200 rounded-xl p-6 text-center shadow-sm">
          <p className="text-rose-700 font-semibold">{error}</p>
        </div>
      ) : (
        <>
          {/* Top 5 Students - Ultra Modern Cards */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-lg border-2 border-slate-100">
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Top 5 Students
              </h4>
              <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                Ranked by overall percentage
              </span>
            </div>

            <div className="space-y-4">
              {filteredTop.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <User className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No results found</p>
                </div>
              ) : (
                filteredTop.map((stu, idx) => {
                  const pct = Number(stu.percentage || 0);
                  const pctRounded = pct.toFixed(2);
                  const photoUrl = studentPhotos[stu.rollNumber];
                  
                  return (
                    <div
                      key={stu.id || stu.rollNumber}
                      className={`group flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer ${getGradeBg(pct)}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {/* Rank Badge */}
                        <div className={`relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-base text-white ${getRankBadge(idx)}`}>
                          {idx === 0 && (
                            <div className="absolute inset-0 rounded-full animate-ping opacity-75 bg-yellow-400"></div>
                          )}
                          <span className="relative z-10">{idx + 1}</span>
                        </div>

                        {/* Student Photo & Info */}
                        <div className="flex items-center gap-3 flex-1">
                          <div className="relative group-hover:scale-110 transition-transform duration-300">
                            {photoUrl ? (
                              <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-purple-600 rounded-full blur opacity-50"></div>
                                <img 
                                  src={photoUrl} 
                                  alt={stu.studentName}
                                  className="relative w-12 h-12 rounded-full object-cover border-3 border-white shadow-lg"
                                />
                              </div>
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-200 to-purple-300 flex items-center justify-center border-3 border-white shadow-lg">
                                <User className="w-6 h-6 text-indigo-700" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-900 truncate text-base">
                              {stu.studentName || `${stu.firstName || ''} ${stu.surname || ''}`.trim() || 'Unknown'}
                            </div>
                            <div className="text-xs text-slate-600 font-medium flex items-center gap-2">
                              <span className="bg-white/70 px-2 py-0.5 rounded-md">{stu.rollNumber}</span>
                              {stu.division && (
                                <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md">{stu.division}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Percentage Badge */}
                      <div className="relative">
                        <div className={`px-5 py-2.5 rounded-xl font-bold text-base ${getGradeColor(pct)} bg-white/90 backdrop-blur-sm border-3 shadow-lg group-hover:shadow-xl transition-shadow`}>
                          {pctRounded}%
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Subject-wise Analysis - Enhanced Modern Cards */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-lg border-2 border-slate-100">
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Subject-wise Performance Analysis
              </h4>
              <span className="text-xs font-bold text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">
                {allResults.length} students
              </span>
            </div>

            {subjectStats.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <BarChart3 className="w-16 h-16 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No subject data available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {subjectStats.map((subject) => (
                  <div
                    key={subject.code}
                    className="group bg-gradient-to-br from-white to-slate-50 p-5 rounded-xl border-2 border-slate-200 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                  >
                    {/* Subject Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h5 className="font-bold text-slate-900 text-base mb-1">
                          {subject.shortName}
                        </h5>
                        <p className="text-xs text-slate-500 line-clamp-2 font-medium">{subject.name}</p>
                      </div>
                      <div className={`px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${
                        subject.passPercent >= 75 ? 'bg-gradient-to-br from-emerald-400 to-teal-600 text-white' :
                        subject.passPercent >= 60 ? 'bg-gradient-to-br from-amber-400 to-orange-600 text-white' :
                        'bg-gradient-to-br from-rose-400 to-red-600 text-white'
                      }`}>
                        {subject.passPercent}%
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-slate-600 font-semibold mb-2">
                        <span>Pass Rate</span>
                        <span>{subject.passed}/{subject.totalStudents}</span>
                      </div>
                      <div className="relative w-full bg-slate-200 rounded-full h-3 overflow-hidden shadow-inner">
                        <div
                          className={`absolute h-full rounded-full transition-all duration-500 ${
                            subject.passPercent >= 75 ? 'bg-gradient-to-r from-emerald-400 to-teal-600' :
                            subject.passPercent >= 60 ? 'bg-gradient-to-r from-amber-400 to-orange-600' :
                            'bg-gradient-to-r from-rose-400 to-red-600'
                          }`}
                          style={{ width: `${subject.passPercent}%` }}
                        >
                          <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-100 shadow-sm">
                        <div className="text-slate-600 font-semibold">Avg Marks</div>
                        <div className="font-bold text-slate-900 text-base mt-1">
                          {subject.averageMarks}/{subject.maxMarks}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-3 rounded-lg border border-emerald-100 shadow-sm">
                        <div className="text-slate-600 font-semibold">Avg %</div>
                        <div className={`font-bold text-base mt-1 ${getGradeColor(subject.averagePercent)}`}>
                          {subject.averagePercent}%
                        </div>
                      </div>
                    </div>

                    {/* Full Marks Count */}
                    <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 rounded-lg border-2 border-amber-200 shadow-sm overflow-hidden">
                      <button
                        onClick={() => setExpandedSubject(expandedSubject === subject.code ? null : subject.code)}
                        className="w-full p-3 flex items-center gap-2 hover:bg-amber-100/50 transition-colors"
                      >
                        <div className="p-1.5 bg-gradient-to-br from-amber-400 to-orange-600 rounded-lg shadow-sm">
                          <Award className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-xs font-semibold text-slate-600">
                            Students with Full Marks
                          </div>
                          <div className="text-lg font-bold text-slate-900 mt-0.5">
                            {subject.fullMarksCount} {subject.fullMarksCount === 1 ? 'student' : 'students'}
                          </div>
                        </div>
                        {subject.fullMarksCount > 0 && (
                          <ChevronDown className={`w-4 h-4 text-amber-600 transition-transform duration-300 ${
                            expandedSubject === subject.code ? 'rotate-180' : ''
                          }`} />
                        )}
                      </button>
                      
                      {/* Expandable List */}
                      {expandedSubject === subject.code && subject.fullMarksStudents.length > 0 && (
                        <div className="border-t-2 border-amber-200 bg-white p-3 space-y-2 transition-all duration-300 ease-in-out">
                          {subject.fullMarksStudents.map((student, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100 hover:shadow-sm transition-all"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                  {idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-bold text-slate-800 truncate">
                                    {student.studentName}
                                  </div>
                                  <div className="text-xs text-slate-600 font-medium">
                                    {student.rollNumber}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-md">
                                {student.division}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Stats */}
          <div className="flex items-center justify-between text-xs bg-white/60 backdrop-blur-sm p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-emerald-400 to-teal-600 shadow-sm" />
                <span className="text-slate-700 font-semibold">≥75% (Excellent)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-amber-400 to-orange-600 shadow-sm" />
                <span className="text-slate-700 font-semibold">60-74% (Good)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-rose-400 to-red-600 shadow-sm" />
                <span className="text-slate-700 font-semibold">&lt;60% (Need Support)</span>
              </div>
            </div>
            <div className="text-slate-500 font-semibold">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TopperformersWidget;
